import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Users, 
  Crown, 
  MessageCircle, 
  FileText,
  DollarSign,
  Activity,
  TrendingUp,
  Calendar,
  Search,
  Filter,
  Download,
  RefreshCw,
  Eye,
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  BarChart3,
  User,
  Settings,
  Shield,
  Trash2,
  ExternalLink,
  Globe,
  UserPlus,
  Paperclip,
  Image as ImageIcon
} from 'lucide-react';

const AdminPortal = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [showTicketUpdateForm, setShowTicketUpdateForm] = useState(false);
  const [showUserUpdateForm, setShowUserUpdateForm] = useState(false);
  const [ticketUpdateData, setTicketUpdateData] = useState({ status: '', response: '' });
  const [ticketAttachFile, setTicketAttachFile] = useState(null);
  const ticketFileInputRef = React.useRef(null);

  // Download file with auth header — triggers save dialog, no new tab needed
  const downloadTicketAttachment = async (ticketId, fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/support/ticket/${ticketId}/attachment/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('Could not download file: ' + e.message);
    }
  };

  // Load image inline with auth header — returns base64 data URL
  const [inlineImages, setInlineImages] = useState({});
  const loadInlineImage = async (ticketId, fileId) => {
    const key = `${ticketId}-${fileId}`;
    if (inlineImages[key]) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/support/ticket/${ticketId}/attachment/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => setInlineImages(prev => ({ ...prev, [key]: reader.result }));
      reader.readAsDataURL(blob);
    } catch (e) { /* ignore */ }
  };
  const [userUpdateData, setUserUpdateData] = useState({ 
    full_name: '', 
    is_active: true, 
    role: 'user', 
    subscription_plan: 'free',
    new_password: ''
  });
  const [updating, setUpdating] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    email: '',
    full_name: '',
    password: '',
    subscription_plan: 'free'
  });
  const [adminData, setAdminData] = useState({
    stats: {
      totalUsers: 0,
      activeUsers: 0,
      totalSubscribers: 0,
      totalDocuments: 0,
      totalSupportTickets: 0,
      monthlyRevenue: 0
    },
    users: [],
    supportTickets: [],
    documents: [],
    publicNotices: []
  });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showConversationModal, setShowConversationModal] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminData();
      fetchPublicNotices();
    }
  }, [user]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      console.log('Fetching admin data with token:', token ? 'Token present' : 'No token');
      console.log('User role:', user?.role);

      // Fetch admin stats, users, and tickets
      const [statsResponse, usersResponse, ticketsResponse] = await Promise.all([
        fetch(`${backendUrl}/api/admin/stats`, { headers }),
        fetch(`${backendUrl}/api/admin/users?limit=1000`, { headers }),
        fetch(`${backendUrl}/api/admin/support-tickets`, { headers })
      ]);

      console.log('Response statuses:', {
        stats: statsResponse.status,
        users: usersResponse.status,
        tickets: ticketsResponse.status
      });

      if (statsResponse.ok) {
        const stats = await statsResponse.json();
        console.log('Stats data:', stats);
        setAdminData(prev => ({ ...prev, stats }));
      } else {
        const errorText = await statsResponse.text();
        console.error('Stats fetch failed:', statsResponse.status, errorText);
      }

      if (usersResponse.ok) {
        const users = await usersResponse.json();
        console.log('Users data:', users.length, 'users loaded');
        console.log('First user sample:', users[0]);
        console.log('First user monthly_usage:', users[0]?.monthly_usage);
        setAdminData(prev => ({ ...prev, users }));
      } else {
        const errorText = await usersResponse.text();
        console.error('Users fetch failed:', usersResponse.status, errorText);
      }

      if (ticketsResponse.ok) {
        const supportTickets = await ticketsResponse.json();
        console.log('Support tickets data:', supportTickets.length, 'tickets loaded');
        setAdminData(prev => ({ ...prev, supportTickets }));
      } else {
        const errorText = await ticketsResponse.text();
        console.error('Support tickets fetch failed:', ticketsResponse.status, errorText);
      }

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicNotices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/notices/public`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const notices = await response.json();
        setAdminData(prev => ({ ...prev, publicNotices: notices }));
      } else {
        console.error('Failed to fetch public notices');
      }
    } catch (error) {
      console.error('Error fetching public notices:', error);
    }
  };

  const deletePublicNotice = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this public notice? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/notices/${noticeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Remove the notice from local state
        setAdminData(prev => ({
          ...prev,
          publicNotices: prev.publicNotices.filter(notice => notice.id !== noticeId)
        }));
        alert('Public notice deleted successfully');
      } else {
        const error = await response.json();
        alert(`Failed to delete notice: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting notice:', error);
      alert('Error deleting notice. Please try again.');
    }
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <Shield className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You need administrator privileges to access this portal.</p>
      </div>
    );
  }

  const getPlanColor = (plan) => {
    switch (plan?.toLowerCase()) {
      case 'free': return 'bg-gray-100 text-gray-800';
      case 'truth_seeker': return 'bg-blue-100 text-blue-800';
      case 'sovereign_shield': return 'bg-purple-100 text-purple-800';
      case 'defender_of_the_people': return 'bg-gold-100 text-gold-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserUpdateData({ 
      full_name: user.full_name || '', 
      is_active: user.is_active, 
      role: user.role || 'user', 
      subscription_plan: user.subscription_plan || 'free',
      new_password: '' // Always start with empty password field
    });
    setShowUserModal(true);
  };

  const handleResetUsage = async (user) => {
    const confirmMessage = `Reset monthly usage for ${user.full_name || user.email}?\n\nThis will reset:\n- Legal Matters to 0\n- Documents Generated to 0\n\nThe user will be able to create new legal matters according to their plan limits.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Use email as identifier since user.id might be null
    const userId = user.id || user.email;
    
    if (!userId) {
      alert('❌ Error: Unable to identify user (no ID or email found)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/${encodeURIComponent(userId)}/reset-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Success!\n\nUsage reset for: ${result.user_email}\nReset by: ${result.reset_by}\n\nNew usage:\n- Legal Matters: 0\n- Documents: 0`);
        fetchAdminData(); // Refresh the user list
      } else {
        const error = await response.json();
        alert(`❌ Failed to reset usage: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resetting user usage:', error);
      alert('Error resetting usage. Please try again.');
    }
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket || (!ticketUpdateData.status && !ticketUpdateData.response && !ticketAttachFile)) {
      alert('Please provide a response, attach a file, or change the status');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');

      if (ticketAttachFile) {
        // Send reply with file attachment
        const formData = new FormData();
        formData.append('file', ticketAttachFile);
        formData.append('message', ticketUpdateData.response || '');
        const attachRes = await fetch(`${backendUrl}/api/support/ticket/${selectedTicket.ticket_id}/reply-with-attachment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (!attachRes.ok) {
          const err = await attachRes.json().catch(() => ({}));
          alert(`Attachment failed: ${err.detail || 'Unknown error'}`);
          setUpdating(false);
          return;
        }
        // Also update status if changed
        if (ticketUpdateData.status) {
          await fetch(`${backendUrl}/api/admin/support-tickets/${selectedTicket.ticket_id}`, {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: ticketUpdateData.status })
          });
        }
      } else {
        // Text-only reply / status update
        const response = await fetch(`${backendUrl}/api/admin/support-tickets/${selectedTicket.ticket_id}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(ticketUpdateData)
        });
        if (!response.ok) {
          const errorData = await response.json();
          alert(`Failed to update ticket: ${errorData.detail || 'Unknown error'}`);
          setUpdating(false);
          return;
        }
      }

      // Refresh ticket data and keep modal open
      const ticketsRes = await fetch(`${backendUrl}/api/admin/support-tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ticketsRes.ok) {
        const freshTickets = await ticketsRes.json();
        setAdminData(prev => ({ ...prev, supportTickets: freshTickets }));
        const updated = freshTickets.find(t => t.ticket_id === selectedTicket.ticket_id);
        if (updated) setSelectedTicket(updated);
      }
      setTicketUpdateData(prev => ({ ...prev, response: '' }));
      setTicketAttachFile(null);
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Error updating ticket. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const deleteSupportTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this support ticket? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-tickets/${ticketId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Remove the ticket from local state
        setAdminData(prev => ({
          ...prev,
          supportTickets: prev.supportTickets.filter(ticket => ticket.ticket_id !== ticketId)
        }));
        alert('Support ticket deleted successfully');
      } else {
        const error = await response.json();
        alert(`Failed to delete ticket: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting support ticket:', error);
      alert('Error deleting support ticket. Please try again.');
    }
  };

  const bulkDeleteTickets = async () => {
    if (selectedTickets.length === 0) return;
    if (!window.confirm(`Delete ${selectedTickets.length} selected ticket(s)? This cannot be undone.`)) return;
    
    const token = localStorage.getItem('token');
    let deleted = 0;
    for (const ticketId of selectedTickets) {
      try {
        const response = await fetch(`${backendUrl}/api/admin/support-tickets/${ticketId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (response.ok) deleted++;
      } catch (e) { /* continue */ }
    }
    setAdminData(prev => ({
      ...prev,
      supportTickets: prev.supportTickets.filter(t => !selectedTickets.includes(t.ticket_id))
    }));
    setSelectedTickets([]);
    alert(`${deleted} ticket(s) deleted successfully.`);
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTickets.length === adminData.supportTickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(adminData.supportTickets.map(t => t.ticket_id));
    }
  };


  const handleCreateUser = async () => {
    // Validate form data
    if (!createUserData.email || !createUserData.full_name || !createUserData.password) {
      alert('Please fill in all required fields (email, full name, and password)');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createUserData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createUserData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`User created successfully!\n\nEmail: ${result.user.email}\nName: ${result.user.full_name}\nPlan: ${result.user.subscription_plan}\n\nA welcome email has been sent to the user.`);
        
        // Reset form and close modal
        setCreateUserData({
          email: '',
          full_name: '',
          password: '',
          subscription_plan: 'free'
        });
        setShowCreateUserModal(false);
        
        // Refresh user list
        fetchAdminData();
      } else {
        const error = await response.json();
        alert(`Failed to create user: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) {
      alert('No user selected');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare update data without password
      const { new_password, ...updateData } = userUpdateData;
      
      // Update user profile first
      const response = await fetch(`${backendUrl}/api/admin/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update user: ${errorData.detail || 'Unknown error'}`);
      }

      let successMessage = 'User updated successfully!';

      // If password is provided, change it separately
      if (new_password && new_password.trim()) {
        if (new_password.length < 8) {
          throw new Error('Password must be at least 8 characters long');
        }

        const passwordResponse = await fetch(`${backendUrl}/api/admin/users/${selectedUser.id}/change-password`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: new_password })
        });

        if (!passwordResponse.ok) {
          const errorData = await passwordResponse.json();
          throw new Error(`Failed to change password: ${errorData.detail || 'Unknown error'}`);
        }

        successMessage = 'User updated and password changed successfully!';
      }

      alert(successMessage);
      setShowUserUpdateForm(false);
      setUserUpdateData({ 
        full_name: '', 
        is_active: true, 
        role: 'user', 
        subscription_plan: 'free',
        new_password: ''
      });
      fetchAdminData(); // Refresh data

    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.message || 'Error updating user. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const renderTicketModal = () => (
    <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Support Ticket</DialogTitle>
          <DialogDescription>
            {selectedTicket?.subject} — <Badge className={getStatusColor(selectedTicket?.status)}>{selectedTicket?.status}</Badge>
          </DialogDescription>
        </DialogHeader>
        {selectedTicket && (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            {/* Ticket info summary */}
            <div className="grid grid-cols-2 gap-3 text-sm border-b pb-3">
              <div><span className="text-gray-500">From:</span> {selectedTicket.user_name} ({selectedTicket.user_email})</div>
              <div><span className="text-gray-500">Priority:</span> <Badge className={getPriorityColor(selectedTicket.priority)}>{selectedTicket.priority}</Badge></div>
              <div><span className="text-gray-500">Category:</span> <Badge variant="outline">{selectedTicket.category}</Badge></div>
              <div><span className="text-gray-500">Created:</span> {new Date(selectedTicket.created_at).toLocaleDateString()}</div>
            </div>

            {/* Conversation thread */}
            <div className="flex-1 overflow-y-auto space-y-3 min-h-[200px] max-h-[350px] pr-1" data-testid="ticket-conversation-thread">
              {/* Original message */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-blue-700">{selectedTicket.user_name}</span>
                  <span className="text-xs text-blue-400">{new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>

              {/* Response history */}
              {selectedTicket.responses?.map((resp, idx) => (
                <div key={idx} className={`rounded-lg p-3 ${resp.is_user ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-200 ml-4'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${resp.is_user ? 'text-blue-700' : 'text-amber-700'}`}>
                      {resp.is_user ? `${resp.admin_name} (User)` : `${resp.admin_name || 'Admin'}`}
                    </span>
                    <span className={`text-xs ${resp.is_user ? 'text-blue-400' : 'text-amber-400'}`}>
                      {resp.created_at ? new Date(resp.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{resp.response}</p>
                  {/* Render attachments */}
                  {resp.attachments?.map((att, aidx) => {
                    const isImage = att.content_type?.startsWith('image/');
                    const imgKey = `${selectedTicket.ticket_id}-${att.file_id}`;
                    if (isImage && !inlineImages[imgKey]) loadInlineImage(selectedTicket.ticket_id, att.file_id);
                    return (
                      <div key={aidx} className="mt-2 border rounded p-2 bg-white">
                        {isImage && inlineImages[imgKey] ? (
                          <div>
                            <img src={inlineImages[imgKey]} alt={att.filename} className="max-h-48 rounded" />
                            <button onClick={() => downloadTicketAttachment(selectedTicket.ticket_id, att.file_id, att.filename)} className="text-xs text-blue-600 hover:underline mt-1">
                              Download {att.filename}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => downloadTicketAttachment(selectedTicket.ticket_id, att.file_id, att.filename)}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                          >
                            {isImage ? <ImageIcon className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
                            {att.filename}
                            <span className="text-xs text-gray-400">({(att.size / 1024).toFixed(1)} KB)</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Reply + status controls */}
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
                <Select
                  value={ticketUpdateData.status || selectedTicket.status}
                  onValueChange={(value) => setTicketUpdateData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-40 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder="Type your reply..."
                value={ticketUpdateData.response}
                onChange={(e) => setTicketUpdateData(prev => ({ ...prev, response: e.target.value }))}
                rows={3}
                className="text-sm"
                data-testid="ticket-reply-input"
              />
              {/* Attached file preview */}
              {ticketAttachFile && (
                <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                  <Paperclip className="w-3 h-3 text-blue-600" />
                  <span className="text-blue-800 truncate flex-1">{ticketAttachFile.name}</span>
                  <button onClick={() => setTicketAttachFile(null)} className="text-red-500 hover:text-red-700 text-xs font-bold">X</button>
                </div>
              )}
              <input
                type="file"
                ref={ticketFileInputRef}
                className="hidden"
                accept=".pdf,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 10 * 1024 * 1024) { alert('File too large. Max 10MB.'); return; }
                    setTicketAttachFile(file);
                  }
                  e.target.value = '';
                }}
              />
              <div className="flex items-center justify-between">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => { setShowTicketModal(false); deleteSupportTicket(selectedTicket.ticket_id); }}
                >
                  Delete Ticket
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => ticketFileInputRef.current?.click()} data-testid="ticket-attach-file-btn" title="Attach file">
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setShowTicketModal(false); setTicketUpdateData({ status: '', response: '' }); setTicketAttachFile(null); }}>
                    Close
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleUpdateTicket}
                    disabled={updating || (!ticketUpdateData.status && !ticketUpdateData.response && !ticketAttachFile)}
                    data-testid="ticket-send-reply-btn"
                  >
                    {updating ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  const renderUserModal = () => (
    <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete User Information</DialogTitle>
          <DialogDescription>
            All MongoDB data for user: {selectedUser?.email}
          </DialogDescription>
        </DialogHeader>
        {selectedUser && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">User ID</label>
                <p className="mt-1 text-sm text-gray-900 font-mono">{selectedUser.id || selectedUser.user_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Full Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.full_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone Number</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.phone_number || 'N/A'}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Role</label>
                <p className="mt-1 text-sm text-gray-900">
                  <Badge className={selectedUser.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}>
                    {selectedUser.role || 'user'}
                  </Badge>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">
                  <Badge className={selectedUser.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {selectedUser.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Subscription Plan</label>
                <div className="mt-1">
                  <Badge className={getPlanColor(selectedUser.subscription_plan)}>
                    {selectedUser.subscription_plan || 'Free'}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Subscription Status</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.subscription_status || 'N/A'}</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-md font-semibold mb-3">Stripe Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Stripe Customer ID</label>
                  <p className="mt-1 text-xs text-gray-900 font-mono">{selectedUser.stripe_customer_id || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Stripe Subscription ID</label>
                  <p className="mt-1 text-xs text-gray-900 font-mono">{selectedUser.stripe_subscription_id || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-md font-semibold mb-3">Address Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedUser.address || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">City</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedUser.city || 'N/A'}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">State</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedUser.state || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Zip Code</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedUser.zip_code || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Country</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedUser.country || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-md font-semibold mb-3">Timestamps</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Created At</label>
                  <p className="mt-1 text-xs text-gray-900">
                    {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Updated At</label>
                  <p className="mt-1 text-xs text-gray-900">
                    {selectedUser.updated_at ? new Date(selectedUser.updated_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Login</label>
                  <p className="mt-1 text-xs text-gray-900">
                    {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {selectedUser.preferences && Object.keys(selectedUser.preferences).length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-md font-semibold mb-3">User Preferences</h3>
                <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                  {JSON.stringify(selectedUser.preferences, null, 2)}
                </pre>
              </div>
            )}

            {selectedUser.metadata && Object.keys(selectedUser.metadata).length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-md font-semibold mb-3">Metadata</h3>
                <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                  {JSON.stringify(selectedUser.metadata, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex items-center justify-end space-x-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowUserModal(false)}
              >
                Close
              </Button>
              <Button onClick={() => setShowUserUpdateForm(true)}>
                Manage User
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  const renderTicketUpdateForm = () => (
    <Dialog open={showTicketUpdateForm} onOpenChange={setShowTicketUpdateForm}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Support Ticket</DialogTitle>
          <DialogDescription>
            Update status or add response for ticket: {selectedTicket?.ticket_id}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <Select 
              value={ticketUpdateData.status} 
              onValueChange={(value) => setTicketUpdateData(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Admin Response</label>
            <Textarea
              className="mt-1"
              placeholder="Add your response to the ticket..."
              value={ticketUpdateData.response}
              onChange={(e) => setTicketUpdateData(prev => ({ ...prev, response: e.target.value }))}
              rows={4}
            />
          </div>

          <div className="flex items-center justify-end space-x-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowTicketUpdateForm(false);
                setTicketUpdateData({ status: '', response: '' });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateTicket}
              disabled={updating || (!ticketUpdateData.status && !ticketUpdateData.response)}
            >
              {updating ? 'Updating...' : 'Update Ticket'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderUserUpdateForm = () => (
    <Dialog open={showUserUpdateForm} onOpenChange={setShowUserUpdateForm}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage User</DialogTitle>
          <DialogDescription>
            Update user information for: {selectedUser?.email}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Full Name</label>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={userUpdateData.full_name}
              onChange={(e) => setUserUpdateData(prev => ({ ...prev, full_name: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <Select 
                value={userUpdateData.is_active ? 'active' : 'inactive'} 
                onValueChange={(value) => setUserUpdateData(prev => ({ ...prev, is_active: value === 'active' }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Role</label>
              <Select 
                value={userUpdateData.role} 
                onValueChange={(value) => setUserUpdateData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Subscription Plan</label>
            <Select 
              value={userUpdateData.subscription_plan} 
              onValueChange={(value) => setUserUpdateData(prev => ({ ...prev, subscription_plan: value }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro_se_starter">Pro Se Starter</SelectItem>
                <SelectItem value="justice_seeker">Justice Seeker</SelectItem>
                <SelectItem value="sovereign_shield">Sovereign Shield</SelectItem>
                <SelectItem value="defender_of_the_people">Defender of the People</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-4">
            <label className="text-sm font-medium text-gray-700">Change Password (Optional)</label>
            <input
              type="password"
              placeholder="Enter new password (leave empty to keep current)"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={userUpdateData.new_password}
              onChange={(e) => setUserUpdateData(prev => ({ ...prev, new_password: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-500">
              Minimum 8 characters required. Leave empty to keep current password.
            </p>
          </div>

          <div className="flex items-center justify-end space-x-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowUserUpdateForm(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateUser}
              disabled={updating}
            >
              {updating ? 'Updating...' : 'Update User'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderCreateUserModal = () => (
    <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Add a new user account to the system. A welcome email will be sent to the user.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              placeholder="user@example.com"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={createUserData.email}
              onChange={(e) => setCreateUserData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="John Doe"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={createUserData.full_name}
              onChange={(e) => setCreateUserData(prev => ({ ...prev, full_name: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              placeholder="Enter password"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={createUserData.password}
              onChange={(e) => setCreateUserData(prev => ({ ...prev, password: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-500">
              User will be able to change this password after logging in.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Subscription Plan</label>
            <Select 
              value={createUserData.subscription_plan} 
              onValueChange={(value) => setCreateUserData(prev => ({ ...prev, subscription_plan: value }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="justice_seeker">Justice Seeker ($9.99/month)</SelectItem>
                <SelectItem value="sovereign_shield">Sovereign Shield ($99/month)</SelectItem>
                <SelectItem value="defender_of_the_people">Defender of the People (Lifetime)</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              Subscription will be set to active immediately.
            </p>
          </div>

          <div className="flex items-center justify-end space-x-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateUserModal(false);
                setCreateUserData({
                  email: '',
                  full_name: '',
                  password: '',
                  subscription_plan: 'free'
                });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={updating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updating ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderNoticeModal = () => (
    <Dialog open={showNoticeModal} onOpenChange={setShowNoticeModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Public Notice Details</DialogTitle>
          <DialogDescription>
            Notice ID: {selectedNotice?.id}
          </DialogDescription>
        </DialogHeader>
        {selectedNotice && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Title</label>
              <p className="mt-1 text-lg font-semibold text-gray-900">{selectedNotice.title}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Type</label>
              <div className="mt-1">
                <Badge variant="outline">{selectedNotice.notice_type || 'General'}</Badge>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Content</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-md max-h-60 overflow-y-auto">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedNotice.content}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Created At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedNotice.created_at).toLocaleDateString()} at {new Date(selectedNotice.created_at).toLocaleTimeString()}
                </p>
              </div>
              {selectedNotice.updated_at && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Updated</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(selectedNotice.updated_at).toLocaleDateString()} at {new Date(selectedNotice.updated_at).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowNoticeModal(false)}
              >
                Close
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  setShowNoticeModal(false);
                  deletePublicNotice(selectedNotice.id);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Notice
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminData.stats.totalUsers}</div>
            <p className="text-xs text-muted-foreground">Registered users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminData.stats.activeUsers}</div>
            <p className="text-xs text-muted-foreground">Currently active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subscribers</CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminData.stats.totalSubscribers}</div>
            <p className="text-xs text-muted-foreground">Paid subscribers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminData.stats.totalDocuments}</div>
            <p className="text-xs text-muted-foreground">Generated docs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Support Tickets</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminData.stats.totalSupportTickets}</div>
            <p className="text-xs text-muted-foreground">Open tickets</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${adminData.stats.monthlyRevenue}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {adminData.users.slice(0, 5).map((user, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{user.full_name || user.email}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <Badge className={getPlanColor(user.subscription_plan)}>
                    {user.subscription_plan || 'Free'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Support Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {adminData.supportTickets.slice(0, 5).map((ticket, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium truncate">{ticket.subject}</p>
                    <p className="text-xs text-gray-500">{ticket.user_name}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={getPriorityColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                    <Badge className={getStatusColor(ticket.status)}>
                      {ticket.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg sm:text-2xl font-bold">Users Management</h2>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <Button 
            onClick={() => setShowCreateUserModal(true)}
            className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-4"
          >
            <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
            <span className="hidden sm:inline">Create New User</span>
            <span className="sm:hidden">New User</span>
          </Button>
          <Button variant="outline" onClick={fetchAdminData} className="text-xs h-8 sm:h-9 px-2 sm:px-3">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" className="text-xs h-8 sm:h-9 px-2 sm:px-3" onClick={() => {
            const headers = ['User ID','Full Name','Email','Role','Plan','Status','Subscription Status','Stripe Customer ID','Created','Updated'];
            const rows = adminData.users.map(u => [
              u.id || '',
              (u.full_name || '').replace(/,/g, ' '),
              u.email || '',
              u.role || '',
              u.subscription_plan || '',
              u.is_active ? 'Active' : 'Inactive',
              u.subscription_status || '',
              u.stripe_customer_id || '',
              u.created_at ? new Date(u.created_at).toLocaleDateString() : '',
              u.updated_at ? new Date(u.updated_at).toLocaleDateString() : '',
            ]);
            const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `users_export_${new Date().toISOString().slice(0,10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}>
            <Download className="w-3.5 h-3.5 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* User Search Box */}
      <div className="relative" data-testid="admin-user-search-container">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search users by name or email..."
          value={userSearchQuery}
          onChange={e => setUserSearchQuery(e.target.value)}
          className="pl-10 h-10 text-sm border-gray-300"
          data-testid="admin-user-search"
        />
        {userSearchQuery && (
          <button onClick={() => setUserSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      <Card>
        <CardHeader className="px-3 sm:px-6 py-3">
          <CardTitle className="text-sm">
            {userSearchQuery
              ? `Showing ${adminData.users.filter(u => {
                  const q = userSearchQuery.toLowerCase();
                  return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                }).length} of ${adminData.users.length} Users`
              : `All Users (${adminData.users.length})`
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-2">
            {adminData.users.filter(u => {
              if (!userSearchQuery) return true;
              const q = userSearchQuery.toLowerCase();
              return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
            }).map((user, index) => (
              <div key={index} className="border rounded-lg p-3 space-y-2 bg-white">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <User className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{user.full_name || 'N/A'}</p>
                    <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                  </div>
                  <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-800 text-[9px]' : 'bg-gray-100 text-gray-800 text-[9px]'}>
                    {user.role || 'user'}
                  </Badge>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge className={`text-[9px] ${getPlanColor(user.subscription_plan)}`}>{user.subscription_plan || 'Free'}</Badge>
                  <Badge className={`text-[9px] ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <span className="text-[9px] text-gray-400">{user.subscription_status || ''}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-gray-600">
                  <span>Matters: <strong className="text-blue-600">{user.monthly_usage?.legal_matters ?? 0}</strong>/{user.all_time_usage?.legal_matters ?? 0}</span>
                  <span>Docs: <strong className="text-blue-600">{user.monthly_usage?.documents_generated ?? 0}</strong>/{user.all_time_usage?.documents_generated ?? 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-gray-400">Joined {new Date(user.created_at).toLocaleDateString()}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5" onClick={() => handleViewUser(user)}>
                      <Eye className="w-3 h-3 mr-0.5" /> View
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 text-orange-600" onClick={() => handleResetUsage(user)}>
                      <RefreshCw className="w-3 h-3 mr-0.5" /> Reset
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">User ID</th>
                  <th className="text-left p-2">Full Name</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Role</th>
                  <th className="text-left p-2">Plan</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">
                    <div className="flex items-center gap-2">
                      <span>Usage</span>
                      <span className="text-xs text-gray-400">(Month / All)</span>
                    </div>
                  </th>
                  <th className="text-left p-2">Sub Status</th>
                  <th className="text-left p-2">Stripe ID</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Updated</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminData.users.filter(u => {
                  if (!userSearchQuery) return true;
                  const q = userSearchQuery.toLowerCase();
                  return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                }).map((user, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      <div className="text-xs font-mono max-w-[100px] truncate" title={user.id}>{user.id?.substring(0, 8)}...</div>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium">{user.full_name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="p-2"><div className="max-w-[200px] truncate" title={user.email}>{user.email}</div></td>
                    <td className="p-2"><Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}>{user.role || 'user'}</Badge></td>
                    <td className="p-2"><Badge className={getPlanColor(user.subscription_plan)}>{user.subscription_plan || 'Free'}</Badge></td>
                    <td className="p-2"><Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{user.is_active ? 'Active' : 'Inactive'}</Badge></td>
                    <td className="p-2">
                      <div className="text-xs space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-blue-600">{user.monthly_usage?.legal_matters ?? 0}</span>
                          <span className="text-gray-300">/</span>
                          <span className="font-medium text-gray-700">{user.all_time_usage?.legal_matters ?? 0}</span>
                          <span className="text-gray-400">matters</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-blue-600">{user.monthly_usage?.documents_generated ?? 0}</span>
                          <span className="text-gray-300">/</span>
                          <span className="font-medium text-gray-700">{user.all_time_usage?.documents_generated ?? 0}</span>
                          <span className="text-gray-400">docs</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-2"><span className="text-xs text-gray-600">{user.subscription_status || 'N/A'}</span></td>
                    <td className="p-2"><div className="text-xs font-mono max-w-[120px] truncate" title={user.stripe_customer_id}>{user.stripe_customer_id ? user.stripe_customer_id.substring(0, 12) + '...' : 'N/A'}</div></td>
                    <td className="p-2"><div className="text-xs">{new Date(user.created_at).toLocaleDateString()}<br /><span className="text-gray-500">{new Date(user.created_at).toLocaleTimeString()}</span></div></td>
                    <td className="p-2"><div className="text-xs">{user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}</div></td>
                    <td className="p-2">
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewUser(user)} title="View user details"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleResetUsage(user)} title="Reset monthly usage" className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"><RefreshCw className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSupportTickets = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg sm:text-2xl font-bold">Support Tickets</h2>
        <div className="flex items-center gap-2">
          {selectedTickets.length > 0 && (
            <Button variant="destructive" size="sm" onClick={bulkDeleteTickets} className="text-xs" data-testid="bulk-delete-tickets">
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Delete ({selectedTickets.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchAdminData} className="text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="px-3 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">All Tickets ({adminData.supportTickets.length})</CardTitle>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTickets.length === adminData.supportTickets.length && adminData.supportTickets.length > 0}
                onChange={toggleSelectAll}
                className="rounded"
              />
              Select All
            </label>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-2">
            {adminData.supportTickets.map((ticket) => (
              <div key={ticket.ticket_id} className={`border rounded-lg p-3 space-y-2 ${selectedTickets.includes(ticket.ticket_id) ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selectedTickets.includes(ticket.ticket_id)}
                    onChange={() => toggleTicketSelection(ticket.ticket_id)}
                    className="rounded mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 break-words">{ticket.subject}</p>
                    <p className="text-[10px] text-gray-500">{ticket.user_name} — {ticket.user_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[9px]">{ticket.category}</Badge>
                  <Badge className={`text-[9px] ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</Badge>
                  <Badge className={`text-[9px] ${getStatusColor(ticket.status)}`}>{ticket.status}</Badge>
                  <span className="text-[9px] text-gray-400 ml-auto">{new Date(ticket.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => handleViewTicket(ticket)}>
                    <Eye className="w-3 h-3 mr-1" /> View
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-600" onClick={() => deleteSupportTicket(ticket.ticket_id)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 w-8">
                    <input
                      type="checkbox"
                      checked={selectedTickets.length === adminData.supportTickets.length && adminData.supportTickets.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="text-left p-2">Ticket ID</th>
                  <th className="text-left p-2">Subject</th>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Category</th>
                  <th className="text-left p-2">Priority</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminData.supportTickets.map((ticket, index) => (
                  <tr key={index} className={`border-b hover:bg-gray-50 ${selectedTickets.includes(ticket.ticket_id) ? 'bg-blue-50' : ''}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedTickets.includes(ticket.ticket_id)}
                        onChange={() => toggleTicketSelection(ticket.ticket_id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">{ticket.ticket_id?.substring(0, 8)}...</td>
                    <td className="p-2">
                      <div className="max-w-xs truncate" title={ticket.subject}>{ticket.subject}</div>
                    </td>
                    <td className="p-2">
                      <div>
                        <p className="font-medium">{ticket.user_name}</p>
                        <p className="text-xs text-gray-500">{ticket.user_email}</p>
                      </div>
                    </td>
                    <td className="p-2"><Badge variant="outline">{ticket.category}</Badge></td>
                    <td className="p-2"><Badge className={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge></td>
                    <td className="p-2"><Badge className={getStatusColor(ticket.status)}>{ticket.status}</Badge></td>
                    <td className="p-2">{new Date(ticket.created_at).toLocaleDateString()}</td>
                    <td className="p-2">
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewTicket(ticket)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteSupportTicket(ticket.ticket_id)} className="text-red-600 hover:text-red-800 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderPublicNotices = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Public Notices Management</h2>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={fetchPublicNotices}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Public Notices ({adminData.publicNotices?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Content</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminData.publicNotices?.map((notice, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      <div className="max-w-xs truncate font-medium" title={notice.title}>
                        {notice.title}
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="max-w-md truncate text-gray-600" title={notice.content}>
                        {notice.content}
                      </div>
                    </td>
                    <td className="p-2">
                      <Badge variant="outline">{notice.notice_type || 'General'}</Badge>
                    </td>
                    <td className="p-2">{new Date(notice.created_at).toLocaleDateString()}</td>
                    <td className="p-2">
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedNotice(notice);
                            setShowNoticeModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deletePublicNotice(notice.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'support', label: 'Support Tickets', icon: MessageCircle },
  ];

  return (
    <div className="flex flex-col h-full overflow-x-hidden">
      {/* Header */}
      <div className="p-3 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Admin Portal</h1>
            <p className="text-xs sm:text-base text-gray-600">Manage users, support tickets, and analytics</p>
          </div>
          <Badge className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 text-xs">
            <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            Admin
          </Badge>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 bg-white overflow-x-auto">
        <div className="px-3 sm:px-6">
          <nav className="flex space-x-2 sm:space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2.5 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'support' && renderSupportTickets()}
          </>
        )}
      </div>

      {/* Modals */}
      {renderTicketModal()}
      {renderUserModal()}
      {renderTicketUpdateForm()}
      {renderUserUpdateForm()}
      {renderCreateUserModal()}
    </div>
  );
};

export default AdminPortal;